import { memo, useCallback, useEffect, useState } from "react";
import { Image, Progress, Tooltip } from "@heroui/react";
import { ChevronLeft, ChevronRight, Picture, Plus } from "assets/icons";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { useSession } from "@clerk/clerk-react";
import ButtonNew from "components/buttonNew/buttonNew";
import { useUploadFileNavigation } from "../uploadFileModal/uploadFileNavigation";
import {
  FileUploadField,
  UploadType,
} from "../uploadFileModal/uploadFileModal";
import {
  BuildingPictureLabels,
  FileLabel,
  PictureLabel,
  SecondaryLabel,
  UnitPictureLabels,
} from "shared/types/file";
import { SealCheck, Tag, X } from "@phosphor-icons/react";
import { toast } from "react-toastify";

export enum ExclusionType {
  Building = "building",
  Unit = "unit",
}

interface ImagesContainerProps {
  canSelectCoverImage?: boolean;
  buildingId?: string;
  buildings?: string[];
  unitId?: string;
  excludes?: ExclusionType[];
  customData?: any;
  fileLabel?: FileLabel;
  fileLabels?: FileLabel[] | PictureLabel[];
  secondaryLabel?: SecondaryLabel;
  fileLogIds?: number[] | null;
  fetchOnlyPredefinedFileLogIds?: boolean;
  title?: string;
  canUpload?: boolean;
  page?: number;
  pageSize?: number;
  variant?: "light" | "extended";
  display?: "inline";
  skipFields?: FileUploadField[];
  onImageUploadSuccess?: (customData: any) => any;
  canOpenImage?: boolean;
}

const sortImagesByCoverLabel = (images: any) => {
  return images.sort((a: any, b: any) => {
    const aIsCover =
      a.label.includes(PictureLabel.CoverImage) ||
      a.label.includes(PictureLabel.BuildingCoverImage) ||
      a.label.includes(PictureLabel.UnitCoverImage);
    const bIsCover =
      b.label.includes(PictureLabel.CoverImage) ||
      b.label.includes(PictureLabel.BuildingCoverImage) ||
      b.label.includes(PictureLabel.UnitCoverImage);
    return bIsCover - aIsCover;
  });
};

export const ImagesContainer = memo(
  ({
    canSelectCoverImage = true,
    buildingId,
    unitId,
    buildings,
    excludes,
    customData,
    fileLabel,
    fileLabels,
    secondaryLabel,
    fetchOnlyPredefinedFileLogIds = false,
    fileLogIds = null,
    title,
    canUpload = true,
    onImageUploadSuccess,
    page = 1,
    pageSize = 25,
    variant = "light",
    display,
    skipFields = [],
    canOpenImage = true,
  }: ImagesContainerProps) => {
    const { session } = useSession();
    const [images, setImages] = useState<string[]>([]);
    const [imagesData, setImagesData] = useState<any[]>([]);
    const [exclusionType, setExclusionType] = useState<ExclusionType>();
    const [isImageViewerVisible, setImageViewerVisible] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const activeImage = images[activeIndex];
    const [isLoading, setIsLoading] = useState(false);
    const { navigateToUploadFiles } = useUploadFileNavigation();
    const [selectedFileLabel, setSelectedFileLabel] = useState<FileLabel>();
    const [selectedFileLabels, setSelectedFileLabels] = useState<
      FileLabel | PictureLabel | null
    >(null);
    const [selectedSecondaryLabel, setSelectedSecondaryLabel] =
      useState<SecondaryLabel>();
    const containerStyles =
      display === "inline"
        ? "border-0"
        : "border border-neutral-primary/15 bg-white";

    const placeholderImage = "https://yally.be/uploads/placeholder.jpg";

    useEffect(() => {
      setSelectedFileLabels(fileLabels as any);
    }, [fileLabels]);

    useEffect(() => {
      setSelectedFileLabel(fileLabel);
    }, [fileLabel]);

    useEffect(() => {
      setSelectedSecondaryLabel(secondaryLabel);
    }, [secondaryLabel]);

    useEffect(() => {
      if (excludes && excludes.length > 0) {
        if (excludes.includes(ExclusionType.Building)) {
          setExclusionType(ExclusionType.Building);
        } else if (excludes.includes(ExclusionType.Unit)) {
          setExclusionType(ExclusionType.Unit);
        }
      }
    }, [excludes]);

    useEffect(() => {
      if (fileLogIds && fileLogIds.length > 0) {
        fetchSpecificImages();
      } else if (
        (!fetchOnlyPredefinedFileLogIds && (buildingId || unitId)) ||
        (buildings && buildings.length > 1)
      ) {
        fetchImagesWithLabel();
      }
    }, [
      buildingId,
      unitId,
      fileLabel,
      secondaryLabel,
      page,
      pageSize,
      fileLogIds,
      session,
      fetchOnlyPredefinedFileLogIds,
    ]);

    useEffect(() => {
      const nav = document.getElementById("navBar") as HTMLElement;
      if (isImageViewerVisible) {
        document.body.style.overflow = "hidden";
        nav.style.opacity = "0";
        nav.style.visibility = "hidden";
      } else {
        document.body.style.overflow = "auto";
        nav.style.opacity = "100";
        nav.style.visibility = "visible";
      }
      return () => {
        document.body.style.overflow = "auto";
        nav.style.opacity = "100";
        nav.style.visibility = "visible";
      };
    }, [isImageViewerVisible]);

    // Listen for document refresh events from file upload page
    useEffect(() => {
      const handleRefreshDocuments = () => {
        if (fileLogIds && fileLogIds.length > 0) {
          fetchSpecificImages();
        } else {
          fetchImagesWithLabel();
        }
        if (onImageUploadSuccess) {
          onImageUploadSuccess(customData);
        }
      };

      window.addEventListener('refreshDocuments', handleRefreshDocuments);
      return () => {
        window.removeEventListener('refreshDocuments', handleRefreshDocuments);
      };
    }, [fileLogIds, customData, onImageUploadSuccess]);

    useEffect(() => {
      const handleKeyDown = (event: any) => {
        if (event.key === "Escape") {
          setImageViewerVisible(false);
        } else if (event.key === "ArrowLeft") {
          goToPrev();
        } else if (event.key === "ArrowRight") {
          goToNext();
        }
      };
      if (isImageViewerVisible) {
        window.addEventListener("keydown", handleKeyDown);
      }
      return () => {
        window.removeEventListener("keydown", handleKeyDown);
      };
    }, [isImageViewerVisible, activeIndex]);

    const fetchSpecificImages = useCallback(
      async (incomingFileLogIds?: number[]) => {
        let isMounted = true;
        const controller = new AbortController();
        setIsLoading(true);
        const fileLogIdsToFetch =
          incomingFileLogIds && incomingFileLogIds.length > 0
            ? incomingFileLogIds
            : fileLogIds;
        try {
          const response = await fetchWithAuth(
            `${API_URL}/file/get-specific-images`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ fileLogIds: fileLogIdsToFetch }),
              signal: controller.signal,
            },
            session,
          );
          const images = await response.json();
          const sortedImages = sortImagesByCoverLabel(images);

          console.log(images);
          if (isMounted && sortedImages && sortedImages.length > 0) {
            setImagesData(sortedImages);
            setImages(sortedImages.map((img: any) => img.imageUrl));
            //setActiveImage(imageUrls[0]?.imageUrl);
            setActiveIndex(0);
          }
        } catch (error) {
          console.error("Error fetching specific images:", error);
        } finally {
          if (isMounted) setIsLoading(false);
        }
        return () => {
          isMounted = false;
          controller.abort();
          console.log("Fetch aborted by cleanup");
        };
      },
      [
        buildingId,
        unitId,
        fileLabel,
        excludes,
        page,
        pageSize,
        fileLogIds,
        session,
        fetchOnlyPredefinedFileLogIds,
      ],
    );

    const fetchImagesWithLabel = useCallback(async () => {
      let isMounted = true;
      const controller = new AbortController();
    
      setIsLoading(true);
      try {
        const queryParams = new URLSearchParams({
          buildingId: buildingId || "",
          unitId: unitId || "",
          acquisitionId: (customData && customData.acquisitionId) || "", // Note the correct spelling of 'acquisitionId'
          exclusionType: (exclusionType && exclusionType.toString()) || "",
          fileLabel: fileLabel || selectedFileLabel || "",
          secondaryLabel: secondaryLabel || "",
          page: page.toString(),
          pageSize: pageSize.toString(),
        }).toString();
    
        let response;
        let labels = fileLabels ? fileLabels : selectedFileLabels ? [selectedFileLabels] : [];
    
        // For multiple labels without buildings
        if ((!buildings || buildings.length === 0) && labels && labels.length > 0) {
          console.log("Trying to fetch images for multiple labels");
          response = await fetchWithAuth(
            `${API_URL}/file/get-images?${queryParams}`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ labels, acquisitionId: customData?.acquisitionId }),
              signal: controller.signal,
            },
            session
          );
        } 
        // For single file without buildings
        else if (!buildings || buildings.length === 0) {
          console.log("Trying to fetch images for a singular file");
          response = await fetchWithAuth(
            `${API_URL}/file/get-images?${queryParams}`,
            { method: "GET" },
            session
          );
        }
        // For multiple buildings with labels
        else if (buildings && buildings.length > 0 && labels && labels.length > 0) {
          console.log("Trying to fetch images for multiple buildings with multiple labels");
          response = await fetchWithAuth(
            `${API_URL}/file/get-images-for-buildings?${queryParams}`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ 
                buildings, 
                labels, 
                acquisitionId: customData?.acquisitionId 
              }),
              signal: controller.signal,
            },
            session
          );
        } 
        // For multiple buildings without labels
        else if (buildings && buildings.length > 0) {
          console.log("Trying to fetch images for multiple buildings");
          response = await fetchWithAuth(
            `${API_URL}/file/get-images-for-buildings?${queryParams}`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ 
                buildings, 
                acquisitionId: customData?.acquisitionId 
              }),
              signal: controller.signal,
            },
            session
          );
        }

        const result = await (response as any).json();
        const images = result.data || result;

        if (isMounted && images && images.length > 0) {
          const validImages = images.filter((img: any) => img.imageFound);
          if (validImages.length > 0) {
            const sortedValidImages = sortImagesByCoverLabel(validImages);
            setImagesData(sortedValidImages);
            setImages(sortedValidImages.map((img: any) => img.imageUrl));
            setActiveIndex(0);
          } else {
            // If no valid images are found, set a placeholder
            setImagesData([{ imageUrl: placeholderImage, label: [], imageFound: false }]);
            setImages([placeholderImage]);
            setActiveIndex(0);
          }
        } else {
          // If no images are returned at all, set a placeholder
          setImagesData([{ imageUrl: placeholderImage, label: [], imageFound: false }]);
          setImages([placeholderImage]);
          setActiveIndex(0);
        }
      } catch (error) {
        console.error("Error fetching images:", error);
        // Set placeholder image in case of error
        setImagesData([{ imageUrl: placeholderImage, label: [], imageFound: false }]);
        setImages([placeholderImage]);
        setActiveIndex(0);
      } finally {
        if (isMounted) setIsLoading(false);
      }
      return () => {
        isMounted = false;
        controller.abort();
        console.log("Fetch aborted by cleanup");
      };
    }, [
      buildingId,
      unitId,
      fileLabel,
      page,
      excludes,
      pageSize,
      fileLogIds,
      session,
      fetchOnlyPredefinedFileLogIds,
      customData,
      exclusionType,
      selectedFileLabel,
      secondaryLabel,
      fileLabels,
      selectedFileLabels,
    ]);


    const handleImageClick = (index: any) => {
      console.log("Opening modal for image", index);
      setActiveIndex(index);
      setImageViewerVisible(true);
    };

    const goToPrev = () => {
      if (activeIndex > 0) {
        setActiveIndex(activeIndex - 1);
      }
    };

    const goToNext = () => {
      if (activeIndex < images.length - 1) {
        setActiveIndex(activeIndex + 1);
      }
    };

    const prevArrowOpacity =
      activeIndex > 0 ? "opacity-100" : "opacity-50 cursor-not-allowed";
    const nextArrowOpacity =
      activeIndex < images.length - 1
        ? "opacity-100"
        : "opacity-50 cursor-not-allowed";

    const selectAsCoverImage = async (imageUrl: string) => {
      const updatedImagesData = imagesData.map((img) => {
        if (img.imageUrl === imageUrl) {
          // Add the 'PictureLabel.CoverImage' to the labels array if it's not already there
          const updatedLabels = img.label.includes(PictureLabel.CoverImage)
            ? img.label
            : [...img.label, PictureLabel.CoverImage];
          return { ...img, label: updatedLabels };
        } else {
          // Remove the 'PictureLabel.CoverImage' from other images
          const filteredLabels = img.label.filter(
            (label: any) => label !== PictureLabel.CoverImage,
          );
          return { ...img, label: filteredLabels };
        }
      });

      try {
        const selectedImageData: any = {
          fileLog: {},
          entity: { type: "", id: "" },
        };
        selectedImageData.fileLog = updatedImagesData.find(
          (img) => img.imageUrl === imageUrl,
        );
        selectedImageData.entity =
          customData && customData.acquisitionId
            ? { type: "acquisition", id: customData.acquisitionId }
            : unitId
              ? { type: "unit", id: unitId }
              : { type: "building", id: buildingId };

        console.log(selectedImageData);
        if (selectedImageData) {
          const response = await fetchWithAuth(
            `${API_URL}/file/set-cover-image`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(selectedImageData),
            },
            session,
          );

          if (response.ok) {
            // Update the local state with the new images data
            setImagesData(updatedImagesData);
            toast.success("Cover afbeelding succesvol aangepast");
          } else {
            toast.error(
              "Er is iets misgegaan bij het instellen van de cover afbeelding",
            );
          }
        }
      } catch (error) {
        console.error("Error setting cover image:", error);
      }
    };

    const isCoverImage = (activeImage: string) => {
      const selectedImageData = imagesData.find(
        (img) => img.imageUrl === activeImage,
      );
      return selectedImageData
        ? selectedImageData.label.includes(PictureLabel.CoverImage)
        : false;
    };

    const handleUploadClick = (): boolean => {
      const hasBuildingContext =
        Boolean(buildingId) ||
        Boolean(unitId) ||
        (Array.isArray(buildings) && buildings.length > 0);

      if (customData?.acquisitionId && !hasBuildingContext) {
        toast.error(
          "Vervolledig eerst het acquisitiedossier zodat er gebouwen zijn aangemaakt waaraan de foto's kunnen worden gekoppeld.",
        );
        return false;
      }

      try {
        navigateToUploadFiles({
          defaultFileLabel: fileLabels
            ? (fileLabels[0] as FileLabel)
            : selectedFileLabel,
          defaultSecondaryLabel: selectedSecondaryLabel,
          buildings: buildings,
          building: buildingId ? { id: buildingId } : undefined,
          incomingCustomData: customData,
          uploadType: UploadType.PICTURES,
          unit: unitId ? { id: unitId } : undefined,
          customLabels: selectedFileLabels
            ? selectedFileLabel
            : selectedFileLabel
              ? [selectedFileLabel]
              : unitId
                ? Object.values(UnitPictureLabels).sort()
                : buildingId
                  ? Object.values(BuildingPictureLabels).sort()
                  : [],
          skipFields: skipFields,
          variant: variant,
        });
        return true;
      } catch (error) {
        console.error("Error opening upload modal:", error);
        toast.error(
          "Er is iets misgegaan bij het openen van het upload-venster.",
        );
        throw error;
      }
    };

    return (
      <div className={`${containerStyles} flex flex-col gap-2 rounded-md`}>
        {isLoading && <Progress size="md" color="success" />}
        {display !== "inline" && (
          <header className="flex items-center justify-between border-b border-neutral-primary/15 p-2.5">
            <div className="flex items-center gap-1.5">
              <Picture className="h-[18px] w-[18px]" />
              <p className="text-lg">{title ? title : "Foto's"}</p>
            </div>
          </header>
        )}
        <div className="flex flex-col gap-5 p-2.5">
          <div className="relative flex">
            {display !== "inline" && (
              <a className="absolute left-0 top-0 flex items-center gap-1 rounded-full border border-neutral-primary/10 p-2 text-sm font-medium shadow-md">
                <Tag size={14} weight="duotone" />
                <p>{fileLabel}</p>
              </a>
            )}
            {display !== "inline" && canSelectCoverImage && (
              <Tooltip content="Stel in als coverfoto">
                <a
                  onClick={() => {
                    selectAsCoverImage(activeImage);
                  }}
                  className={`absolute right-0 top-0 cursor-pointer rounded-full border border-neutral-primary/10 p-2 text-sm font-medium shadow-md ${isCoverImage(activeImage)
                    ? "bg-moss-primary text-white"
                    : "bg-white text-moss-primary"
                    }`}
                >
                  <SealCheck size={24} weight="duotone" />
                </a>
              </Tooltip>
            )}
            {display !== "inline" && (
              <div className="px-1">
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    goToPrev();
                  }}
                  className="flex h-full items-center text-moss-primary"
                >
                  <ChevronLeft className={`h-8 w-8 ${prevArrowOpacity}`} />
                </a>
              </div>
            )}
            {activeImage && (
              <div className="flex w-full items-center justify-center">
                <img
                  className="max-h-[350px] cursor-pointer overflow-hidden rounded-sm"
                  src={activeImage}
                  onClick={() => {
                    if(!canOpenImage) return;
                    handleImageClick(activeIndex)
                  }}
                  alt="Gallery"
                />
              </div>
            )}
            {display !== "inline" && (
              <div className="px-1">
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    goToNext();
                  }}
                  className="flex h-full items-center text-moss-primary"
                >
                  <ChevronRight className={`h-8 w-8 ${nextArrowOpacity}`} />
                </a>
              </div>
            )}
          </div>
          {pageSize && pageSize > 1 && (
            <div className="flex h-fit flex-nowrap gap-3 overflow-x-auto">
              {images.map((image, index) => (
                <figure
                  key={`image-${index}`}
                  className="inline-flex h-[60px] w-[60px] shrink-0 grow-0 basis-[60px] cursor-pointer items-center justify-center overflow-hidden rounded-sm object-cover duration-200 hover:opacity-70"
                //onClick={() => handleImageClick(index)}
                >
                  <Image
                    key={`image-${index}`}
                    src={image}
                    alt={`Image ${index}`}
                    onClick={() => {
                      if(!canOpenImage) return;
                      handleImageClick(index)
                    }}
                    isBlurred
                    isZoomed
                  />
                </figure>
              ))}
            </div>
          )}
          {pageSize && pageSize == 1 && canUpload && (
            <ButtonNew
              label="Foto's toevoegen"
              icon={<Plus />}
              variant="secondaryNeutral"
              onClick={(e: any) => {
                e.stopPropagation();
                if (!handleUploadClick()) return;
              }}
            />
          )}
          {pageSize && pageSize > 1 && canUpload && (
            <ButtonNew
              label="Foto's toevoegen"
              icon={<Plus />}
              variant="secondaryNeutral"
              onClick={() => {
                if (!handleUploadClick()) return;
              }}
            />
          )}
        </div>
        {isImageViewerVisible && (
          <div className="fixed inset-0 z-50 bg-white/90 backdrop-blur-xs">
            <div className="relative flex h-screen flex-col">
              <div className="absolute right-3 top-3 flex gap-2">
                <Tooltip content="Stel in als coverfoto">
                  <a
                    href=""
                    className="rounded-full border border-neutral-primary/10 bg-white p-2 text-sm font-medium text-moss-primary shadow-md"
                  >
                    <SealCheck size={24} weight="duotone" />
                  </a>
                </Tooltip>
                <Tooltip content="Sluiten">
                  <a
                    href=""
                    onClick={(e) => {
                      e.preventDefault();
                      setImageViewerVisible(false);
                    }}
                    className="rounded-full border border-neutral-primary/10 bg-white p-2 text-sm font-medium text-moss-primary shadow-md"
                  >
                    <X size={24} weight="bold" />
                  </a>
                </Tooltip>
              </div>
              <div className="flex flex-1 items-center">
                <div className="px-3">
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      goToPrev();
                    }}
                    className="flex h-full items-center text-moss-primary"
                  >
                    <ChevronLeft className={`h-8 w-8 ${prevArrowOpacity}`} />
                  </a>
                </div>
                <div className="flex flex-1 items-center justify-center">
                  <Image
                    src={activeImage}
                    alt="Active Image"
                    //isBlurred
                    classNames={{
                      wrapper: "flex items-center justify-center",
                      img: "cursor-pointer max-h-[70vh] overflow-hidden rounded-sm",
                    }}
                  />
                </div>
                <div className="px-3">
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      goToNext();
                    }}
                    className="flex h-full items-center text-moss-primary"
                  >
                    <ChevronRight className={`h-8 w-8 ${nextArrowOpacity}`} />
                  </a>
                </div>
              </div>
              {pageSize && pageSize > 1 && (
                <div className="flex h-fit flex-nowrap gap-3 overflow-x-auto px-6 py-6">
                  {images.map((image, index) => (
                    <figure
                      key={`image-${index}`}
                      className="inline-flex h-[60px] w-[60px] shrink-0 grow-0 basis-[60px] cursor-pointer items-center justify-center overflow-hidden rounded-sm object-cover duration-200 hover:opacity-70"
                    //onClick={() => handleImageClick(index)}
                    >
                      <Image
                        key={`image-${index}`}
                        src={image}
                        alt={`Image ${index}`}
                        onClick={() => {
                          if(!canOpenImage) return;
                          handleImageClick(index)
                        }}
                        isBlurred
                        isZoomed
                      />
                    </figure>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  },
);
